
import { useState } from 'react';
import Layout from '../../../components/feature/Layout';

interface Report {
  id: string;
  nombre: string;
  descripcion: string;
  categoria: string;
  ultimaGeneracion: string;
  frecuencia: string;
  formato: string[];
}

const reportsData: Report[] = [
  {
    id: '1',
    nombre: 'Operaciones por Estado',
    descripcion: 'Resumen de operaciones agrupadas por estado actual',
    categoria: 'Operativo',
    ultimaGeneracion: '2024-01-15 09:30',
    frecuencia: 'Diario',
    formato: ['PDF', 'Excel']
  },
  {
    id: '2',
    nombre: 'Operaciones por Gestor',
    descripcion: 'Carga de trabajo y rendimiento por gestor operativo',
    categoria: 'Productividad',
    ultimaGeneracion: '2024-01-15 08:00',
    frecuencia: 'Semanal',
    formato: ['PDF', 'Excel']
  },
  {
    id: '3',
    nombre: 'Tiempo Promedio por Etapa',
    descripcion: 'Análisis de tiempos de ciclo en cada estado del flujo',
    categoria: 'Eficiencia',
    ultimaGeneracion: '2024-01-14 18:00',
    frecuencia: 'Semanal',
    formato: ['PDF', 'Excel']
  },
  {
    id: '4',
    nombre: 'Operaciones por Aduana',
    descripcion: 'Distribución de operaciones por punto aduanero',
    categoria: 'Operativo',
    ultimaGeneracion: '2024-01-14 12:00',
    frecuencia: 'Mensual',
    formato: ['PDF', 'Excel']
  },
  {
    id: '5',
    nombre: 'Incidencias por Tipo',
    descripcion: 'Análisis de incidencias categorizadas por tipo y gravedad',
    categoria: 'Calidad',
    ultimaGeneracion: '2024-01-13 16:00',
    frecuencia: 'Semanal',
    formato: ['PDF', 'Excel']
  },
  {
    id: '6',
    nombre: 'Backlog Operativo',
    descripcion: 'Operaciones pendientes y en riesgo de vencimiento',
    categoria: 'Operativo',
    ultimaGeneracion: '2024-01-15 10:00',
    frecuencia: 'Diario',
    formato: ['PDF', 'Excel']
  },
  {
    id: '7',
    nombre: 'SLA y Cumplimiento',
    descripcion: 'Indicadores de cumplimiento de tiempos de servicio',
    categoria: 'Eficiencia',
    ultimaGeneracion: '2024-01-12 09:00',
    frecuencia: 'Mensual',
    formato: ['PDF', 'Excel']
  },
  {
    id: '8',
    nombre: 'Documentos Pendientes',
    descripcion: 'Listado de documentos faltantes por operación',
    categoria: 'Documental',
    ultimaGeneracion: '2024-01-15 07:00',
    frecuencia: 'Diario',
    formato: ['PDF', 'Excel']
  }
];

const statsData = {
  operacionesPorEstado: [
    { estado: 'Creada', cantidad: 12, porcentaje: 8 },
    { estado: 'Documentación', cantidad: 28, porcentaje: 19 },
    { estado: 'DUCA', cantidad: 35, porcentaje: 24 },
    { estado: 'Selectivo', cantidad: 18, porcentaje: 12 },
    { estado: 'Liberada', cantidad: 22, porcentaje: 15 },
    { estado: 'Transporte', cantidad: 15, porcentaje: 10 },
    { estado: 'Entrega', cantidad: 8, porcentaje: 5 },
    { estado: 'Cerrada', cantidad: 10, porcentaje: 7 }
  ],
  operacionesPorGestor: [
    { gestor: 'María González', activas: 32, completadas: 145, eficiencia: 94 },
    { gestor: 'Carlos Ramírez', activas: 28, completadas: 132, eficiencia: 91 },
    { gestor: 'Ana Torres', activas: 25, completadas: 128, eficiencia: 89 },
    { gestor: 'Luis Mendoza', activas: 22, completadas: 118, eficiencia: 87 }
  ],
  tiempoPromedio: [
    { etapa: 'Documentación', dias: 2.3 },
    { etapa: 'DUCA', dias: 1.8 },
    { etapa: 'Selectivo', dias: 0.5 },
    { etapa: 'Liberación', dias: 1.2 },
    { etapa: 'Transporte', dias: 2.1 },
    { etapa: 'Entrega', dias: 0.8 }
  ]
};

export default function OperationsReports() {
  const [selectedCategoria, setSelectedCategoria] = useState('todas');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [dateRange, setDateRange] = useState('mes');

  const displayToast = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const getCategoriaColor = (categoria: string) => {
    switch (categoria) {
      case 'Operativo': return 'bg-[#2F80ED]/10 text-[#2F80ED]';
      case 'Productividad': return 'bg-[#10B981]/10 text-[#10B981]';
      case 'Eficiencia': return 'bg-[#F59E0B]/10 text-[#F59E0B]';
      case 'Calidad': return 'bg-[#EF4444]/10 text-[#EF4444]';
      case 'Documental': return 'bg-[#8B5CF6]/10 text-[#8B5CF6]';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredReports = reportsData.filter(r => 
    selectedCategoria === 'todas' || r.categoria === selectedCategoria
  );

  const getEstadoColor = (estado: string) => {
    const colors: Record<string, string> = {
      'Creada': '#6B7280',
      'Documentación': '#F59E0B',
      'DUCA': '#2F80ED',
      'Selectivo': '#8B5CF6',
      'Liberada': '#10B981',
      'Transporte': '#0EA5E9',
      'Entrega': '#14B8A6',
      'Cerrada': '#059669'
    };
    return colors[estado] || '#6B7280';
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A2A43]">Reportes Operativos</h1>
            <p className="text-sm text-[#6B7280] mt-1">Análisis y métricas del Core Operativo Aduanero</p>
          </div>
          <div className="flex items-center gap-3">
            <select 
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]"
            >
              <option value="semana">Última Semana</option>
              <option value="mes">Último Mes</option>
              <option value="trimestre">Último Trimestre</option>
              <option value="año">Último Año</option>
            </select>
            <button className="px-4 py-2.5 border border-gray-200 text-[#6B7280] rounded-md hover:bg-gray-50 cursor-pointer whitespace-nowrap">
              <i className="ri-calendar-line mr-2"></i>Personalizar
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#6B7280]">Total Operaciones</span>
              <div className="w-8 h-8 rounded-lg bg-[#0A2A43]/10 flex items-center justify-center">
                <i className="ri-ship-line text-[#0A2A43]"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-[#0A2A43]">148</p>
            <p className="text-xs text-[#10B981] mt-1"><i className="ri-arrow-up-line"></i> +12% vs mes anterior</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#6B7280]">Tiempo Promedio</span>
              <div className="w-8 h-8 rounded-lg bg-[#2F80ED]/10 flex items-center justify-center">
                <i className="ri-time-line text-[#2F80ED]"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-[#2F80ED]">8.7 días</p>
            <p className="text-xs text-[#10B981] mt-1"><i className="ri-arrow-down-line"></i> -0.5 días vs mes anterior</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#6B7280]">Cumplimiento SLA</span>
              <div className="w-8 h-8 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
                <i className="ri-check-double-line text-[#10B981]"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-[#10B981]">94.2%</p>
            <p className="text-xs text-[#10B981] mt-1"><i className="ri-arrow-up-line"></i> +2.1% vs mes anterior</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#6B7280]">Incidencias</span>
              <div className="w-8 h-8 rounded-lg bg-[#EF4444]/10 flex items-center justify-center">
                <i className="ri-error-warning-line text-[#EF4444]"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-[#EF4444]">12</p>
            <p className="text-xs text-[#10B981] mt-1"><i className="ri-arrow-down-line"></i> -3 vs mes anterior</p>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-2 gap-6">
          {/* Operaciones por Estado */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-base font-semibold text-[#0A2A43]">Operaciones por Estado</h3>
              <button 
                onClick={() => displayToast('Exportando reporte...')}
                className="text-sm text-[#2F80ED] hover:underline cursor-pointer"
              >
                <i className="ri-download-line mr-1"></i>Exportar
              </button>
            </div>
            <div className="space-y-3">
              {statsData.operacionesPorEstado.map((item) => (
                <div key={item.estado} className="flex items-center gap-3">
                  <div className="w-24 text-sm text-[#6B7280]">{item.estado}</div>
                  <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-500"
                      style={{ 
                        width: `${item.porcentaje}%`,
                        backgroundColor: getEstadoColor(item.estado)
                      }}
                    ></div>
                  </div>
                  <div className="w-12 text-sm font-semibold text-[#0A2A43] text-right">{item.cantidad}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Tiempo Promedio por Etapa */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-base font-semibold text-[#0A2A43]">Tiempo Promedio por Etapa</h3>
              <button 
                onClick={() => displayToast('Exportando reporte...')}
                className="text-sm text-[#2F80ED] hover:underline cursor-pointer"
              >
                <i className="ri-download-line mr-1"></i>Exportar
              </button>
            </div>
            <div className="space-y-4">
              {statsData.tiempoPromedio.map((item) => (
                <div key={item.etapa} className="flex items-center justify-between">
                  <span className="text-sm text-[#6B7280]">{item.etapa}</span>
                  <div className="flex items-center gap-3">
                    <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-[#114B7A] to-[#2F80ED] rounded-full"
                        style={{ width: `${(item.dias / 3) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-semibold text-[#0A2A43] w-16 text-right">{item.dias} días</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-[#0A2A43]">Tiempo Total Promedio</span>
                <span className="text-lg font-bold text-[#2F80ED]">8.7 días</span>
              </div>
            </div>
          </div>
        </div>

        {/* Rendimiento por Gestor */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-base font-semibold text-[#0A2A43]">Rendimiento por Gestor</h3>
            <button 
              onClick={() => displayToast('Exportando reporte...')}
              className="text-sm text-[#2F80ED] hover:underline cursor-pointer"
            >
              <i className="ri-download-line mr-1"></i>Exportar
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase">Gestor</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-[#6B7280] uppercase">Operaciones Activas</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-[#6B7280] uppercase">Completadas (Mes)</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-[#6B7280] uppercase">Eficiencia</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-[#6B7280] uppercase">Tendencia</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {statsData.operacionesPorGestor.map((gestor, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#114B7A] to-[#2F80ED] flex items-center justify-center text-white font-semibold text-sm">
                          {gestor.gestor.split(' ').map(n => n[0]).join('')}
                        </div>
                        <span className="text-sm font-medium text-[#0A2A43]">{gestor.gestor}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="text-sm font-semibold text-[#0A2A43]">{gestor.activas}</span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="text-sm font-semibold text-[#0A2A43]">{gestor.completadas}</span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${gestor.eficiencia >= 90 ? 'bg-[#10B981]' : gestor.eficiencia >= 80 ? 'bg-[#F59E0B]' : 'bg-[#EF4444]'}`}
                            style={{ width: `${gestor.eficiencia}%` }}
                          ></div>
                        </div>
                        <span className={`text-sm font-semibold ${gestor.eficiencia >= 90 ? 'text-[#10B981]' : gestor.eficiencia >= 80 ? 'text-[#F59E0B]' : 'text-[#EF4444]'}`}>
                          {gestor.eficiencia}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="inline-flex items-center gap-1 text-sm text-[#10B981]">
                        <i className="ri-arrow-up-line"></i>
                        +{Math.floor(Math.random() * 5) + 1}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Catálogo de Reportes */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-[#0A2A43]">Catálogo de Reportes</h3>
              <div className="flex items-center gap-3">
                <select 
                  value={selectedCategoria}
                  onChange={(e) => setSelectedCategoria(e.target.value)}
                  className="px-4 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]"
                >
                  <option value="todas">Todas las categorías</option>
                  <option value="Operativo">Operativo</option>
                  <option value="Productividad">Productividad</option>
                  <option value="Eficiencia">Eficiencia</option>
                  <option value="Calidad">Calidad</option>
                  <option value="Documental">Documental</option>
                </select>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 p-6">
            {filteredReports.map((report) => (
              <div 
                key={report.id} 
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all cursor-pointer"
                onClick={() => {
                  setSelectedReport(report);
                  setShowPreviewModal(true);
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#EAF2F8] flex items-center justify-center">
                      <i className="ri-file-chart-line text-[#2F80ED] text-xl"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-[#0A2A43]">{report.nombre}</h4>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${getCategoriaColor(report.categoria)}`}>
                        {report.categoria}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-[#6B7280] mb-3">{report.descripcion}</p>
                <div className="flex items-center justify-between text-xs text-[#6B7280]">
                  <span><i className="ri-time-line mr-1"></i>{report.frecuencia}</span>
                  <span><i className="ri-calendar-line mr-1"></i>{report.ultimaGeneracion}</span>
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                  {report.formato.map((fmt) => (
                    <button 
                      key={fmt}
                      onClick={(e) => {
                        e.stopPropagation();
                        displayToast(`Generando ${report.nombre} en ${fmt}...`);
                      }}
                      className={`flex-1 py-1.5 text-xs rounded cursor-pointer ${
                        fmt === 'PDF' 
                          ? 'bg-[#EF4444]/10 text-[#EF4444] hover:bg-[#EF4444]/20' 
                          : 'bg-[#10B981]/10 text-[#10B981] hover:bg-[#10B981]/20'
                      }`}
                    >
                      <i className={`${fmt === 'PDF' ? 'ri-file-pdf-line' : 'ri-file-excel-line'} mr-1`}></i>
                      {fmt}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal Vista Previa Reporte */}
      {showPreviewModal && selectedReport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-[#EAF2F8] flex items-center justify-center">
                  <i className="ri-file-chart-line text-[#2F80ED] text-2xl"></i>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-[#0A2A43]">{selectedReport.nombre}</h2>
                  <p className="text-sm text-[#6B7280]">{selectedReport.descripcion}</p>
                </div>
              </div>
              <button onClick={() => setShowPreviewModal(false)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-xl text-[#6B7280]"></i>
              </button>
            </div>
            <div className="p-6">
              {/* Filtros del Reporte */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-[#0A2A43] mb-3">Parámetros del Reporte</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-[#6B7280] mb-1">Fecha Inicio</label>
                    <input type="date" defaultValue="2024-01-01" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#6B7280] mb-1">Fecha Fin</label>
                    <input type="date" defaultValue="2024-01-15" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#6B7280] mb-1">Agrupación</label>
                    <select className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md">
                      <option>Diario</option>
                      <option>Semanal</option>
                      <option>Mensual</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Vista Previa */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-[#0A2A43]">Vista Previa</h3>
                  <button 
                    onClick={() => displayToast('Actualizando vista previa...')}
                    className="text-sm text-[#2F80ED] hover:underline cursor-pointer"
                  >
                    <i className="ri-refresh-line mr-1"></i>Actualizar
                  </button>
                </div>
                <div className="bg-gray-100 rounded-lg h-64 flex items-center justify-center">
                  <div className="text-center">
                    <i className="ri-bar-chart-grouped-line text-5xl text-[#6B7280] mb-3"></i>
                    <p className="text-sm text-[#6B7280]">Vista previa del reporte</p>
                    <p className="text-xs text-gray-400 mt-1">Los datos se generarán según los parámetros seleccionados</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-between">
              <button 
                onClick={() => displayToast('Programando reporte...')}
                className="px-4 py-2.5 border border-gray-200 text-[#6B7280] rounded-md hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-schedule-line mr-2"></i>Programar Envío
              </button>
              <div className="flex items-center gap-3">
                <button onClick={() => setShowPreviewModal(false)} className="px-4 py-2.5 border border-gray-200 text-[#6B7280] rounded-md hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                  Cancelar
                </button>
                <button 
                  onClick={() => {
                    displayToast(`Generando ${selectedReport.nombre} en PDF...`);
                    setShowPreviewModal(false);
                  }}
                  className="px-4 py-2.5 bg-[#EF4444] text-white rounded-md hover:shadow-md cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-file-pdf-line mr-2"></i>Exportar PDF
                </button>
                <button 
                  onClick={() => {
                    displayToast(`Generando ${selectedReport.nombre} en Excel...`);
                    setShowPreviewModal(false);
                  }}
                  className="px-4 py-2.5 bg-[#10B981] text-white rounded-md hover:shadow-md cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-file-excel-line mr-2"></i>Exportar Excel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {showToast && (
        <div className="fixed bottom-6 right-6 bg-[#10B981] text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 z-50 animate-pulse">
          <i className="ri-check-circle-line text-xl"></i>
          <span className="font-medium">{toastMessage}</span>
        </div>
      )}
    </Layout>
  );
}
